import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { StopIcon, MusicNoteIcon, PlayIcon, ClockIcon, ChevronDownIcon } from '../shared/Icons';
import { APP_ENV } from '../../utils/appEnv';
import { useI18n } from '../../i18n';
import { ROUND_TIMING } from '../../utils/tournament';
import { formatClock, useRoundClock, type ClockPhase } from '../../hooks/useRoundClock';
import { playGong, unlockRingSounds } from '../../lib/ringSounds';
import { local, session } from '../../lib/safeStorage';
import { logBackgroundError } from '../../observability';
import { getBoutTimer, putBoutTimer } from '../../api/client';
import { useToast } from '../../hooks/useToast';

// Sarama MP3s served from Azure Blob Storage (prod/staging containers)
const isStaging = APP_ENV === 'staging';
const BLOB_BASE = `https://lmfappstorage.blob.core.windows.net/${isStaging ? 'staging' : 'prod'}/sarama`;
// Local dev: fall back to static files
// VITE_API_URL is a switch here, not an origin (#664): docker-compose sets it and `public/sarama` does not exist, so `lmf up` plays the blob files.
const BASE = import.meta.env.VITE_API_URL ? BLOB_BASE : '/sarama';
const WAI_KRU = `${BASE}/Wai_Khru.mp3`;
/** The Sarama has a track for rounds 1, 2 and 3. For round 4 and round 5 of
 *  a 5-round bout (#602), the jury plays the track of round 3. */
const ROUND_TRACKS = 3;
const roundTrack = (round: number) => `${BASE}/${round}_round.mp3`;
const FOLD_KEY = 'lmf:round-clock-folded';
/** #601 — the choice to show the timer on the ring display, for the bout in this tab, as the timer state is. */
const SHOWN_KEY = (boutId: string) => `lmf:bout-timer-shown:${boutId}`;

const TIME_COLOR: Record<ClockPhase, string> = {
  ready: 'text-gray-900', running: 'text-gray-900', paused: 'text-amber-600', done: 'text-red-600',
};

/**
 * The Sarama player and the backup timer of the jury (#600), on the live
 * bout. The music is the main part (operator, 2026-10-14): the jury plays
 * each track by hand. The timer serves when the timekeeper, who strikes the
 * gong (§18.1), leaves for a short time. It is not the official time. It
 * counts the round time of the class once. It has no round count and no
 * rest: the jury starts each round by hand.
 *
 * The music follows the timer, an LMF decision (IFMA has no rule for music
 * during the rounds). Start sounds the gong. Stop pauses the timer and the
 * music. Continue resumes both. At 0:00 the gong sounds and the music stops.
 * The jury knows TIME and CHOCK, so the buttons do not name them (operator,
 * 2026-10-14).
 *
 * A browser plays a sound only after an action of the user. Each click on the
 * page (a tap, a mouse click or a key) prepares the gong, so a timer that a
 * reload restored can sound its gong at 0:00. A pointerdown is no action of
 * the user for a touch.
 *
 * #601 — with "Show on the ring display" on (off for each new bout), the card
 * sends each change of the timer to the server. The ring display counts down
 * from it. The stopwatch of the timekeeper stays the official time.
 */

/** No answer, or a gateway or a server that is away for a moment: a send that
 *  failed in this way goes again once. A refusal (403, 409) does not. It keeps
 *  its own axios test (#661, F13): an axios error with no answer is transient,
 *  and a value that is no axios error (a bug of the code) is not. */
function isTransient(err: unknown): boolean {
  if (!axios.isAxiosError(err)) return false;
  const status = err.response?.status;
  return status === undefined || status === 502 || status === 503 || status === 504;
}
export default function RoundClock({ boutId, ageGroup }: { boutId: string; ageGroup: string | null | undefined }) {
  const { t } = useI18n();
  const { showError } = useToast();
  // A bout of no known class runs on the round time of Elite.
  const roundSec = (ROUND_TIMING[ageGroup ?? ''] ?? ROUND_TIMING.Elite).roundSec;

  // One audio element at a time. `playing` is the file that plays.
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);
  // The music that Stop paused, so Continue resumes it. Music that the jury
  // stopped by hand, a track that ended, or another track that the jury
  // played during the stop does not start again.
  const pausedRef = useRef<{ audio: HTMLAudioElement; file: string } | null>(null);
  const [folded, setFolded] = useState(() => local.get(FOLD_KEY) === '1');

  function playFile(file: string) {
    const current = audioRef.current;
    if (current) { current.pause(); current.currentTime = 0; }
    pausedRef.current = null;
    const audio = new Audio(file);
    audio.onended = () => { if (audioRef.current === audio) setPlaying(null); };
    audio.play().catch(failedPlay(audio));
    audioRef.current = audio;
    setPlaying(file);
  }

  /** A track that the browser refuses or cannot load (a missing file, no
   *  network) does not show as playing: the button shows Play again. The
   *  context names the file, so the telemetry tells the tracks apart. */
  function failedPlay(audio: HTMLAudioElement) {
    return (err: unknown) => {
      // A pause or another track interrupts a play: that is no failure.
      if (err instanceof DOMException && err.name === 'AbortError') return;
      if (audioRef.current === audio) setPlaying(null);
      logBackgroundError(`RoundClock:play:${audio.src.split('/').pop() ?? ''}`)(err);
    };
  }

  function stopMusic() {
    const current = audioRef.current;
    if (current) { current.pause(); current.currentTime = 0; }
    pausedRef.current = null;
    setPlaying(null);
  }

  const clock = useRoundClock(boutId, roundSec, {
    onPause: () => {
      const current = audioRef.current;
      pausedRef.current = current && playing ? { audio: current, file: playing } : null;
      current?.pause();
      setPlaying(null);
    },
    onResume: () => {
      const paused = pausedRef.current;
      pausedRef.current = null;
      if (!paused || audioRef.current !== paused.audio) return;
      paused.audio.play().catch(failedPlay(paused.audio));
      setPlaying(paused.file);
    },
    onEnd: late => { stopMusic(); if (!late) playGong(); },
  });

  // #601 — the timer on the ring display. Off for each new bout. The choice
  // lives in this tab, and the shown state lives on the server.
  const storedChoice = useRef<string | null | undefined>(undefined);
  if (storedChoice.current === undefined) {
    storedChoice.current = session.get(SHOWN_KEY(boutId));
  }
  const [shown, setShown] = useState(() => storedChoice.current === '1');
  /** The `shown` that the server holds, as far as this card knows. Only an
   *  answer changes it, so a failed Off goes again at the next change. */
  const serverShown = useRef(false);
  /** The jury changed the box: a late read of the server does not undo it. */
  const touched = useRef(false);
  /** The read of the server ticked the box: no send for that change. */
  const skipSend = useRef(false);
  // The effect sets it again: StrictMode runs a cleanup and the effect once more on a mount.
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  // A send reads the state at the moment of the send. A queued send gives the
  // time left at the send, not at the change that queued it.
  const latest = useRef(() => ({ shown, phase: clock.phase, left_ms: Math.round(clock.leftNow()) }));
  latest.current = () => ({ shown, phase: clock.phase, left_ms: Math.round(clock.leftNow()) });
  const sync = useRef({ busy: false, dirty: false, retried: false });
  /** One request at a time, then the newest state (logic-reviewer §15). An
   *  older state never arrives after a newer one, and fast taps send no copy.
   *  The buttons stay free, because the timer runs on this device. A send
   *  that the network lost goes again once, after 2 s. */
  async function push() {
    const q = sync.current;
    q.dirty = true;
    if (q.busy) return;
    q.busy = true;
    try {
      while (q.dirty) {
        q.dirty = false;
        const body = latest.current();
        try {
          await putBoutTimer(boutId, body);
          serverShown.current = body.shown;
          q.retried = false;
        } catch (err) {
          showError(err);
          if (isTransient(err) && !q.retried) {
            q.retried = true;
            window.setTimeout(() => { if (alive.current) void push(); }, 2000);
          }
        }
      }
    } finally { q.busy = false; }
  }
  // A send for each change of the choice or of the phase only: a running
  // timer sends nothing between, because the ring display counts down by
  // itself. An Off goes out while the server can still show the timer: it
  // holds it, or a send that shows it runs now.
  useEffect(() => {
    session.set(SHOWN_KEY(boutId), shown ? '1' : '0'); // With the storage off, this page view only.
    if (skipSend.current) { skipSend.current = false; return; }
    if (!shown && !serverShown.current && !sync.current.busy) return;
    void push();
  }, [boutId, shown, clock.phase]);
  // A second tab or device of the jury starts with the box off. When the
  // server shows the timer, tick the box with no send: the TV keeps the timer
  // of the other tab until the jury acts on this card (#601 review).
  useEffect(() => {
    if (storedChoice.current !== null) return;
    let live = true;
    getBoutTimer(boutId)
      .then(({ data }) => {
        if (!live || touched.current || !data.shown) return;
        serverShown.current = true;
        skipSend.current = true;
        setShown(true);
      })
      .catch(logBackgroundError('RoundClock:timer-read'));
    return () => { live = false; };
  }, [boutId]);

  // The music stops when the card closes: the verdict makes the bout scored,
  // or the page goes to the next bout. Before #600 the music played on.
  useEffect(() => () => { audioRef.current?.pause(); }, []);

  // A click anywhere on the page prepares the gong: after a reload, the jury
  // can tap the score table only.
  useEffect(() => {
    document.addEventListener('click', unlockRingSounds, true);
    return () => document.removeEventListener('click', unlockRingSounds, true);
  }, []);

  function toggleFold() {
    const next = !folded;
    setFolded(next);
    local.set(FOLD_KEY, next ? '1' : '0'); // With the storage off, the choice lasts for this page view only.
  }

  function start() {
    unlockRingSounds();
    playGong();
    clock.start();
  }

  /** Stops the timer and gives the full time again. The music does not
   *  change: it has its own Stop. */
  function reset() {
    pausedRef.current = null;
    clock.reset();
  }

  const tracks = [
    { label: 'Wai Kru', file: WAI_KRU },
    ...Array.from({ length: ROUND_TRACKS }, (_, i) => ({ label: `R${i + 1}`, file: roundTrack(i + 1) })),
  ];

  // The jury taps these at ringside: 44 px touch targets, like the other
  // actions of the officials (operator, 2026-09-25).
  return (
    <div className="card overflow-hidden" data-testid="round-clock">
      <button type="button" onClick={toggleFold} aria-expanded={!folded} data-testid="round-clock-toggle"
        className="w-full px-3 py-2 min-h-11 flex items-center justify-between gap-2 text-left">
        <span className="font-semibold text-sm text-gray-900 flex items-center gap-1.5">
          <MusicNoteIcon className="w-4 h-4 text-amber-600" /> Sarama <span className="font-normal text-gray-400">· {t.bout.timer}</span>
        </span>
        <span className="flex items-center gap-2">
          {folded && clock.phase !== 'ready' && (
            <span className={`font-mono font-bold tabular-nums ${TIME_COLOR[clock.phase]}`}>{formatClock(clock.remainingMs)}</span>
          )}
          <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${folded ? '' : 'rotate-180'}`} />
        </span>
      </button>

      {!folded && (<>
        {/* The Stop of the music is always there, so the row does not jump,
            and greyed out when nothing plays. */}
        <div className="px-3 pb-2 flex items-center gap-1.5" data-testid="sarama-card">
          {tracks.map((track, idx) => (
            <button key={track.file} type="button" data-testid={`sarama-track-${idx}`} aria-pressed={playing === track.file}
              onClick={() => (playing === track.file ? stopMusic() : playFile(track.file))}
              className={`btn min-h-11 flex-1 justify-center px-1 text-xs whitespace-nowrap ${playing === track.file ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              {playing === track.file ? <StopIcon className="w-3.5 h-3.5" /> : <PlayIcon className="w-3.5 h-3.5" />}
              {track.label}
            </button>
          ))}
          <button type="button" onClick={stopMusic} disabled={playing === null} data-testid="sarama-stop"
            aria-label={t.bout.saramaStop} title={t.bout.saramaStop}
            className="btn-outline-red min-h-11 min-w-11 justify-center px-0">
            <StopIcon className="w-4 h-4" />
          </button>
        </div>

        <div className="px-3 py-2 border-t border-gray-100 flex items-center gap-2"
          data-testid="round-clock-face" data-phase={clock.phase}>
          <ClockIcon className="w-4 h-4 text-gray-400 shrink-0" />
          <span data-testid="round-clock-time" className={`w-21 font-mono font-black text-3xl leading-none tabular-nums ${TIME_COLOR[clock.phase]}`}>
            {formatClock(clock.remainingMs)}
          </span>
          {(clock.phase === 'ready' || clock.phase === 'done') && (
            <button type="button" data-testid="round-clock-start" onClick={start}
              className="btn-primary min-h-11 flex-1 justify-center">{t.bout.timerStart}</button>
          )}
          {clock.phase === 'running' && (
            <button type="button" data-testid="round-clock-stop" onClick={clock.pause}
              className="btn min-h-11 flex-1 justify-center bg-amber-500 text-white hover:bg-amber-600">{t.bout.timerStop}</button>
          )}
          {clock.phase === 'paused' && (
            <button type="button" data-testid="round-clock-continue" onClick={clock.resume}
              className="btn-primary min-h-11 flex-1 justify-center">{t.bout.timerContinue}</button>
          )}
          <button type="button" data-testid="round-clock-reset" onClick={reset} disabled={clock.phase === 'ready'}
            className="btn-outline min-h-11 justify-center">{t.bout.timerReset}</button>
        </div>
        <label className="px-3 pb-2 min-h-11 flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
          <input type="checkbox" data-testid="round-clock-show" checked={shown}
            onChange={e => { touched.current = true; setShown(e.target.checked); }} className="w-5 h-5 accent-amber-500" />
          {t.bout.timerOnDisplay}
        </label>
      </>)}
    </div>
  );
}
